import { MediaType } from '@pubstack/common/src/media-types'
import { FunctionComponent, ReactNode } from 'react'
import { MediaTypeBadge } from '~/components/MediaTypeBadge'

export type BidderMediaTypeRow = {
  key: string
  /** A node rather than a string, so callers can keep their own affordances — an out-of-sync marker, a chip with its tooltip. */
  label: ReactNode
  /**
   * The media types the bidder's SEAT is granted by the rule — `banner | video | native`.
   *
   * Deliberately not an ADM stack output's `adUnitMediaTypes`, which is a different vocabulary
   * (`Banner | Outstream | Instream | Skin | Native`) describing what the ad unit accepts rather
   * than what the rule grants the seat. Rendering the two in one list would read as the same fact.
   */
  mediaTypes: MediaType[]
}

type Props = {
  rows: BidderMediaTypeRow[]
  emptyLabel?: ReactNode
}

/**
 * Bidders with the media types their seat is granted, shared by the bidder rule flyout and the
 * stack flyout so the two read the same. The media types were previously either absent (One's stack
 * flyout listed bare names) or only reachable on hover (Node's chip tooltip).
 */
export const BidderMediaTypeList: FunctionComponent<Props> = ({ rows, emptyLabel = 'No bidder' }) => {
  if (rows.length === 0) return <span className="text-sm text-slate-500">{emptyLabel}</span>

  return (
    <ul className="flex w-full flex-col gap-1">
      {rows.map(({ key, label, mediaTypes }) => (
        <li key={key} className="flex items-center justify-between gap-4 text-sm">
          <span className="min-w-0 truncate">{label}</span>
          <span className="flex shrink-0 gap-1">
            {mediaTypes.length > 0 ? (
              /* `plain`: nothing on this list can be clicked, so there is no unselected half for a filled
                 ring to distinguish itself from. Every media type here is one the rule grants, and a row
                 of solid circles spent a lot of ink saying so — the symbols alone, in the row's own
                 colour, say the same thing and let the label beside them stay the loudest part.

                 Size is what holds them back rather than colour: well down from the text they sit under
                 the bidder name without going grey, which on a row that is already grey when its seat is
                 disabled would have been a second thing the same shade says.

                 Through `iconSize`, not a class: `Icon` sizes itself from its own props, and a Tailwind
                 utility aimed at the svg loses to its unlayered Emotion rules. */
              mediaTypes.map((mediaType) => <MediaTypeBadge key={mediaType} mediaType={mediaType} variant="plain" iconSize="16px" />)
            ) : (
              // An aliased label has no seat of its own, so the rule grants it nothing directly.
              <span className="text-slate-400">—</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  )
}
